import supabase from './supabase';
import { sanitizeFileName } from './data-factory';

const SHOW_IMAGES_BUCKET = 'gallery-show-images';
const STORAGE_BASE = `https://api.showrunner.co/storage/v1/object/public/${SHOW_IMAGES_BUCKET}/`;
const PREVIEW_PDFS_BUCKET = 'show-preview-pdfs';

export const MAX_PREVIEW_PDF_BYTES = 50 * 1024 * 1024;
export const MAX_PREVIEW_PDF_LABEL = '50MB';

/** @returns {Promise<string>} the uploaded file's storage path within the bucket */
async function uploadShowFile(bucket, showId, file) {
  const path = `${showId}/${crypto.randomUUID()}_${sanitizeFileName(file.name)}`;
  const { error } = await supabase.storage.from(bucket).upload(path, file);
  if (error) throw error;
  return path;
}

const OUTLIER_MAX_DIMENSION = 3000;

const COMPRESSION_OPTIONS = {
  maxSizeMB: 2,
  alwaysKeepResolution: true,
  initialQuality: 0.85,
  useWebWorker: true,
};

/**
 * Uploads one image file to the show's storage folder.
 * @returns {Promise<string>} the public URL of the uploaded image
 */
export async function uploadShowImage(showId, file) {
  const path = await uploadShowFile(SHOW_IMAGES_BUCKET, showId, file);
  return `${STORAGE_BASE}${path}`;
}

export function uploadShowPreviewPdf(showId, file) {
  return uploadShowFile(PREVIEW_PDFS_BUCKET, showId, file);
}

export async function removeShowPreviewPdf(path) {
  const { error } = await supabase.storage.from(PREVIEW_PDFS_BUCKET).remove([path]);
  if (error) console.error('Failed to remove preview PDF from storage:', error);
}

export function getShowPreviewPdfUrl(path) {
  return supabase.storage.from(PREVIEW_PDFS_BUCKET).getPublicUrl(path).data.publicUrl;
}

export async function removeShowImages(urls) {
  const paths = urls
    .filter(url => url.startsWith(STORAGE_BASE))
    .map(url => url.replace(STORAGE_BASE, ''));
  if (paths.length === 0) return;
  const { error } = await supabase.storage.from(SHOW_IMAGES_BUCKET).remove(paths);
  if (error) console.error('Failed to remove old images from storage:', error);
}

function toMB(bytes) {
  return (bytes / (1024 * 1024)).toFixed(2);
}

function getImageDimensions(file) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve({ width: img.naturalWidth, height: img.naturalHeight });
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error(`Could not read dimensions for ${file.name}`));
    };
    img.src = url;
  });
}

export async function compressImage(file, onProgress) {
  let isOutlier = false;
  try {
    const { width, height } = await getImageDimensions(file);
    isOutlier = Math.max(width, height) > OUTLIER_MAX_DIMENSION;
  } catch (error) {
    console.error('Could not read image dimensions, compressing at default settings:', error);
  }

  const options = isOutlier
    ? { ...COMPRESSION_OPTIONS, maxWidthOrHeight: OUTLIER_MAX_DIMENSION }
    : COMPRESSION_OPTIONS;
  try {
    const { default: imageCompression } = await import('browser-image-compression');
    const compressed = await imageCompression(file, { ...options, onProgress });
    const savedPct = Math.round((1 - compressed.size / file.size) * 100);
    console.log(
      `[image-compression] ${file.name}: ${toMB(file.size)}MB -> ${toMB(compressed.size)}MB (${savedPct}% smaller)`
    );
    return compressed;
  } catch (error) {
    console.error('Image compression failed, using original file:', error);
    return file;
  }
}
